import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { FormatDateOptions } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useMatch, useNavigate, useParams } from 'react-router'
import { Dispatch } from 'redux'
import styled, { css } from 'styled-components'

import { Box } from '../../components/Box'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { Button } from '../../components/Button'
import { CenterChildren } from '../../components/Center'
import { DelayRender } from '../../components/DelayRender'
import { Flex } from '../../components/Flex'
import { Image } from '../../components/Image'
import { InfoBoxWithButtonIcon } from '../../components/InfoBox'
import { Shape3 } from '../../components/Shapes/Shape3'
import { Space } from '../../components/Space'
import { Spin, SpinSize } from '../../components/Spin'
import { Tabs } from '../../components/Tabs'
import { Title, Text } from '../../components/Text'
import { brkmax, themeTitleFontSize } from '../../components/theme'
import { CompanyDetailReportsProps } from '../../containers/CompanyDetail/Reports'
import { useApiData } from '../../hooks/useApiData'
import { useMatchRoute } from '../../hooks/useMatchRoute'
import { CompanyDetailActions, getCompanyByIdentifier, isCompanyLoading } from '../../redux/companyDetail'
import { CompanyDetail, Company } from '../../redux/companyDetail/types'
import {
  CompanyDetailPageUmbracoConfig,
  mapCompanyDetailPageUmbracoConfig,
  getOfficialDocumentsCountries
} from '../../redux/config'
import { CompanyDetailPageUmbracoConfigResponse } from '../../redux/config/mappers'
import { RootState } from '../../redux/types'
import { getRegistrationChannel, isUserLoggedIn } from '../../redux/user/selectors'
import { Routes } from '../../routes/routes'
import { createTFunction, getCompanyCountry } from '../../utils/helpers'
import { Dictionary } from '../../utils/types'
import { getCountryCode, getPreferredProductsEnabled } from '../../redux/config/selectors'
import { PreferredProductsResponse, usePreferredProducts } from '../../hooks/usePreferredProducts'
import { parseGetParams } from '../../utils/routes'

import { CompanyDetailFields, CompanyDetailPageConfig, Tab, useCompanyDetailConfig } from './Configuration/config'

export const StyledBreadcrumbs = styled(Breadcrumbs)`
  padding: 0;
`

export const BackButton = styled(Button)`
  max-width: 180px;
  & > div {
    justify-content: start !important;
  }
`

const CompanyTitle = styled(Title)`
  ${brkmax(
    0,
    css`
      span {
        font-size: ${themeTitleFontSize('large')};
      }
    `
  )}
`

export const Container = styled.div`
  position: relative;
  z-index: 1;
  padding-top: 60px;

  ${brkmax(
    1,
    css`
      padding-top: 24px;
    `
  )}
`

export const BackgroundShapeContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 350px;
  height: 350px;
  transform: translateX(65%) translateY(-22%);

  ${brkmax(
    1,
    css`
      transform: translateX(45%) translateY(-22%);
    `
  )}

  ${brkmax(
    0,
    css`
      top: 165px;
      width: 200px;
      height: 200px;
      transform: translateX(38%) translateY(11%);
    `
  )}
`

type PageContainerProps = {
  children: React.ReactNode[] | React.ReactNode
}

export const PageContainer = ({ children }: PageContainerProps) => (
  <>
    <BackgroundShapeContainer>
      <Shape3 />
    </BackgroundShapeContainer>
    <Container>{children}</Container>
  </>
)

type LoadingStateIndicatorProps = {
  loading: boolean
  company?: Company
  children: React.ReactNode
}
export const LoadingStateIndicator = ({ loading, company, children }: LoadingStateIndicatorProps) => {
  const { t } = useTranslation(['companyDetail', 'core'])
  if (!company) {
    return (
      <PageContainer>
        {loading && (
          <CenterChildren pt={5}>
            <Spin delay={250} type="primary" size={SpinSize.ExtraLarge} />
          </CenterChildren>
        )}
        {!loading && (
          <DelayRender delay={1000}>
            <Flex pt="100px">
              <InfoBoxWithButtonIcon
                icon={<Image width="40px" height="40px" type="alertCircleBlue" />}
                title={t('NotFoundTitle')}
                text={t('NotFoundText')}
                button={{
                  text: t('core:ReloadPage'),
                  onClick: () => window.location.reload()
                }}
              />
            </Flex>
          </DelayRender>
        )}
      </PageContainer>
    )
  }
  return <PageContainer>{children}</PageContainer>
}

export const interpolationData = (
  company: Company | undefined,
  formatAddress: (company: Company) => string,
  formatDate: (value: string | number | Date | undefined, opts?: FormatDateOptions | undefined) => string
) => {
  if (!company) return {}
  return {
    ...company,
    companyName: company.name,
    companyAddress: formatAddress(company),
    lineOfBusiness: company.lineOfBusiness,
    taxId: company.taxId,
    incorporationDate: formatDate(company.incorporationDate, {
      dateStyle: 'medium'
    }),
    contactPhone: company.contactPhone,
    legalForm: company.legalForm,
    registrationNumber: company.registrationNumber,
    enterpriceId: company.enterpriseId,
    latestFinancialStatements: company.latestFinancialStatements
  }
}

function shouldDisplayBanner(userRegistrationType: string, bannerVisibilityTypes?: string[]) {
  if (_.isEmpty(bannerVisibilityTypes)) {
    return true
  }

  if (!userRegistrationType) {
    return _.includes(bannerVisibilityTypes, 'Anonymous')
  }

  if (userRegistrationType === 'BRQ') {
    return _.includes(bannerVisibilityTypes, 'SelfRegisteredUsers')
  }

  if (userRegistrationType === 'AXE') {
    return _.includes(bannerVisibilityTypes, 'Subscribers')
  }

  // eslint-disable-next-line no-console
  console.warn(`Unknown admx.registration-channel value ${userRegistrationType}`)

  return false
}

export type CompanyDetailPageProps = { config?: typeof useCompanyDetailConfig }

export type UseCompanyDetailDataParams = {
  config?: (countryCode: string, domainCode: string) => CompanyDetailPageConfig
}

export type UseCompanyDetailDataRes = {
  companyIdentifier: string
  companyDetail: CompanyDetail | undefined
  loading: boolean
  activeTab: number
  setActiveTab: (n: number) => void
  generateFields: (x: Company) => CompanyDetailFields
  tabs: [Tab<CompanyDetailReportsProps>, Tab<any>, Tab<any>, Tab<any>]
  fromSearch: ReturnType<typeof useMatch> | null | undefined
  dispatch: Dispatch<any>
  tc: (key: string, params?: Dictionary<React.ReactText> | undefined) => string
  umbracoPageConfig: CompanyDetailPageUmbracoConfig | undefined
  t: TFunction<('companyDetail' | 'core')[]>
  displayCompanyDescription?: boolean
  formatAddress: (company: Company) => string
  preferredProductsData: PreferredProductsResponse | undefined
  refreshPreferredProducts: () => void
  loadingPreferredProducts: boolean
}

export function useCompanyDetailData({
  config = useCompanyDetailConfig
}: UseCompanyDetailDataParams): UseCompanyDetailDataRes {
  const { matchRoute } = useMatchRoute()
  const dispatch = useDispatch()
  const { companyIdentifier, slug } = useParams()
  const { search } = useLocation()
  const { companyName } = parseGetParams(search)
  const formerName = companyName ? decodeURIComponent(companyName.toString()) : slug
  const countryCode: string = useSelector(getCountryCode)
  const isLoggedIn: boolean = useSelector(isUserLoggedIn)
  const companyCountryCode: string = getCompanyCountry(companyIdentifier!)
  const { generateFields, tabs, displayCompanyDescription, formatAddress } = config(companyCountryCode, countryCode)
  const officialDocumentsCountries = useSelector(getOfficialDocumentsCountries)
  const viewOfficialDocuments = _.includes(officialDocumentsCountries, companyCountryCode)
  const { t } = useTranslation(['companyDetail', 'core'])

  const [activeTab, setActiveTab] = useState<number>(tabs[0].key)

  const { data: umbracoPageConfigResponse, loading: metaLoading } = useApiData<CompanyDetailPageUmbracoConfigResponse>(
    {
      url: '/api/content/CompanyDetailPageConfig',
      method: 'GET'
    },
    { umbracoCall: true }
  )

  const umbracoPageConfig = mapCompanyDetailPageUmbracoConfig(umbracoPageConfigResponse)

  const loadDocumentsAsynchronously = umbracoPageConfig
    ? _.includes(umbracoPageConfig.officialDocuments?.asyncLoadingCountries, companyCountryCode)
    : undefined

  useEffect(() => {
    if (loadDocumentsAsynchronously !== undefined) {
      isLoggedIn && viewOfficialDocuments
        ? loadDocumentsAsynchronously
          ? dispatch(CompanyDetailActions.loadCompanyByState({ companyIdentifier: companyIdentifier!, formerName }))
          : dispatch(
              CompanyDetailActions.loadCompany({
                companyIdentifier: companyIdentifier!,
                includeOfficialDocuments: true,
                formerName
              })
            )
        : dispatch(CompanyDetailActions.loadCompany({ companyIdentifier: companyIdentifier!, formerName }))
    }
  }, [dispatch, companyIdentifier, loadDocumentsAsynchronously, isLoggedIn, viewOfficialDocuments, formerName])

  const companyDetail = useSelector((state: RootState) =>
    getCompanyByIdentifier(state, { companyIdentifier: companyIdentifier! })
  )
  const loading = useSelector((state: RootState) => isCompanyLoading(state, { companyIdentifier: companyIdentifier! }))

  const previousLocation: string = useLocation().state?.previousLocation

  const fromSearch = matchRoute(previousLocation, Routes.Search)

  const preferredProductsEnabled = useSelector(getPreferredProductsEnabled)
  const { preferredProductsData, preferredProductsLoading, refreshPreferredProducts } = usePreferredProducts(
    companyCountryCode,
    !preferredProductsEnabled || !isLoggedIn || !companyCountryCode
  )

  return {
    companyIdentifier: companyIdentifier!,
    companyDetail,
    loading: loading || metaLoading || (preferredProductsLoading && !preferredProductsData),
    activeTab,
    setActiveTab,
    generateFields,
    tabs,
    fromSearch,
    dispatch,
    tc: createTFunction('companyDetail:', t),
    umbracoPageConfig,
    t,
    displayCompanyDescription,
    formatAddress,
    preferredProductsData,
    refreshPreferredProducts,
    loadingPreferredProducts: preferredProductsLoading
  }
}

type LayoutProps = {
  companyIdentifier: string
  children: React.ReactNode
  fromSearch: boolean
  companyDetail?: CompanyDetail
  tabs: CompanyDetailPageConfig['tabs']
  activeTab: number
  setActiveTab: ReturnType<typeof useCompanyDetailData>['setActiveTab']
  loading: boolean
  umbracoPageConfig?: CompanyDetailPageUmbracoConfig
  companyDescription?: string
  preferredProductsData: PreferredProductsResponse | undefined
  refreshPreferredProductsData: () => void
  loadingPreferredProducts: boolean
}

export const Layout = ({
  companyIdentifier,
  children,
  fromSearch,
  companyDetail,
  tabs,
  activeTab,
  setActiveTab,
  loading,
  umbracoPageConfig,
  companyDescription,
  preferredProductsData,
  refreshPreferredProductsData,
  loadingPreferredProducts
}: LayoutProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['companyDetail'])

  const userRegistrationChannel = useSelector(getRegistrationChannel)

  const filteredTabs = _.filter(tabs, tab => !_.isEmpty(tab.component))

  const company = companyDetail?.company

  const FirstTabComponent = tabs[0].component
  const firstTabRenderDeliveryOptionLabel = tabs[0].componentProps?.renderDeliveryOptionLabel

  return (
    <LoadingStateIndicator company={company} loading={loading}>
      <Space direction="vertical" flexDirection="column" space="20px">
        <StyledBreadcrumbs
          items={[
            {
              text: t('Search')
            },
            {
              text: company?.name || ''
            }
          ]}
        />
        {fromSearch && (
          <BackButton
            variant="transparent"
            onClick={() => navigate(-1)}
            icon={<Image type="leftBlue" />}
            padding="3px 20px 3px 0"
            iconPlacement="left"
          >
            {t('BackToSearch')}
          </BackButton>
        )}
        <CompanyTitle level="extraLarge" withImage>
          {company?.name}
        </CompanyTitle>
        {children}
        {companyDescription && (
          <Flex flexDirection="column" pb={30}>
            <Title level="large" padding={{ bottom: '16px' }}>
              {t('Description')}
            </Title>
            <Text fontSize="14px" color="primary" block pt="4px">
              {companyDescription}
            </Text>
          </Flex>
        )}
        <Flex>
          <Box width={1}>
            {_.size(filteredTabs) >= 2 && (
              <Tabs tabs={filteredTabs} activeTabKey={activeTab} onChange={key => setActiveTab(key)} mobileAsSlider />
            )}
            {activeTab === tabs[0].key &&
              FirstTabComponent &&
              firstTabRenderDeliveryOptionLabel &&
              umbracoPageConfig && (
                <FirstTabComponent
                  reports={company?.reports || []}
                  companyIdentifier={companyIdentifier}
                  companyDetail={companyDetail}
                  renderDeliveryOptionLabel={firstTabRenderDeliveryOptionLabel}
                  officialDocumentsConfig={umbracoPageConfig.officialDocuments}
                  showPriceForAnonymousUsers={umbracoPageConfig.canShowPriceForAnonymousUsers}
                  preferredProductsData={preferredProductsData}
                  refreshPreferredProductsData={refreshPreferredProductsData}
                  loadingPreferredProducts={loadingPreferredProducts}
                  {...tabs[0].componentProps}
                />
              )}
          </Box>
        </Flex>

        {umbracoPageConfig?.banner &&
          shouldDisplayBanner(userRegistrationChannel, umbracoPageConfig?.roleVisibility) && (
            <InfoBoxWithButtonIcon
              title={umbracoPageConfig.banner.title}
              text={<div dangerouslySetInnerHTML={{ __html: umbracoPageConfig.banner.description }} />}
              icon={
                umbracoPageConfig.banner.image ? (
                  <Image src={umbracoPageConfig.banner.image?.url} width="42px" height="42px" />
                ) : null
              }
              link={umbracoPageConfig.banner.link}
              iconPosition={umbracoPageConfig.banner.icon}
            />
          )}
      </Space>
    </LoadingStateIndicator>
  )
}
